import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { InputTextModule } from 'primeng/inputtext';
import {
  MockPathwayPattyService,
  PathwayPattyConversation,
  PathwayPattyMessage,
} from '../../services-mock/mock-pathway-patty.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-pathway-patty',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    AvatarModule,
    InputTextModule,
  ],
  template: `
    <div class="patty-container">
      <div class="patty-header">
        <div class="patty-participant">
          <p-avatar
            label="PP"
            shape="circle"
            size="large"
            styleClass="patty-avatar-header"
          ></p-avatar>
          <div class="participant-info">
            <div class="participant-name">Pathway Patty</div>
            <div class="participant-subtitle">Your Career Coach</div>
          </div>
        </div>
      </div>

      <div class="patty-messages" #messagesContainer>
        <div
          *ngFor="let message of conversation?.messages"
          class="message"
          [class.sent]="message.senderId === 'user'"
          [class.received]="message.senderId === 'patty'"
        >
          <div class="message-content">
            <div class="message-avatar" *ngIf="message.senderId === 'patty'">
              <p-avatar
                label="PP"
                shape="circle"
                size="normal"
                styleClass="patty-avatar-message"
              ></p-avatar>
            </div>
            <div class="message-bubble">
              <div class="message-text" [innerHTML]="formatMessageText(message.text)"></div>
              <div class="message-time">{{ formatMessageTime(message.timestamp) }}</div>
              <button
                *ngIf="message.suggestedCategory"
                pButton
                type="button"
                [label]="'Explore ' + message.suggestedCategory"
                icon="pi pi-arrow-right"
                (click)="exploreCategory(message.suggestedCategory!)"
                class="explore-category-button"
              ></button>
            </div>
            <div class="message-avatar" *ngIf="message.senderId === 'user'">
              <p-avatar
                [label]="currentUserInitials"
                shape="circle"
                size="normal"
              ></p-avatar>
            </div>
          </div>
        </div>
      </div>

      <div class="patty-input-container">
        <input
          type="text"
          pInputText
          [(ngModel)]="newMessage"
          (keyup.enter)="sendMessage()"
          placeholder="Type your response..."
          class="patty-input"
          [disabled]="waitingForResponse"
        />
        <button
          pButton
          type="button"
          icon="pi pi-send"
          (click)="sendMessage()"
          [disabled]="!newMessage.trim() || waitingForResponse"
          class="send-button"
        ></button>
      </div>
    </div>
  `,
  styles: [
    `
      .patty-container {
        display: flex;
        flex-direction: column;
        height: calc(100vh - 60px);
        max-width: 1200px;
        margin: 0 auto;
        background-color: #ffffff;
      }

      .patty-header {
        display: flex;
        align-items: center;
        padding: 1rem 1.5rem;
        border-bottom: 1px solid #e5e5e5;
        background-color: #ffffff;
      }

      .patty-participant {
        display: flex;
        align-items: center;
        gap: 1rem;
        flex: 1;
      }

      .participant-info {
        display: flex;
        flex-direction: column;
      }

      .participant-name {
        font-weight: 600;
        font-size: 1.1rem;
        color: #000000;
      }

      .participant-subtitle {
        font-size: 0.9rem;
        color: #666666;
      }

      .patty-messages {
        flex: 1;
        overflow-y: auto;
        padding: 1.5rem;
        background-color: #f5f5f5;
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .message {
        display: flex;
        width: 100%;
      }

      .message.sent {
        justify-content: flex-end;
      }

      .message.received {
        justify-content: flex-start;
      }

      .message-content {
        display: flex;
        align-items: flex-end;
        gap: 0.5rem;
        max-width: 70%;
      }

      .message.sent .message-content {
        flex-direction: row-reverse;
      }

      .message-bubble {
        background-color: #ffffff;
        border-radius: 12px;
        padding: 0.75rem 1rem;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      }

      .message.sent .message-bubble {
        background-color: #0066cc;
        color: #ffffff;
      }

      .message-text {
        font-size: 0.95rem;
        line-height: 1.4;
        margin-bottom: 0.25rem;
      }

      .message-text strong {
        color: #0066cc;
        font-weight: 600;
      }

      .message.sent .message-text strong {
        color: #ffffff;
      }

      .message-time {
        font-size: 0.75rem;
        opacity: 0.7;
        margin-top: 0.25rem;
      }

      .message-avatar {
        flex-shrink: 0;
      }

      .explore-category-button {
        margin-top: 0.75rem;
        background: #0066cc;
        color: #ffffff;
        border: none;
        font-size: 0.9rem;
      }

      .explore-category-button:hover {
        background: #0052a3;
      }

      .patty-input-container {
        display: flex;
        gap: 0.5rem;
        padding: 1rem 1.5rem;
        border-top: 1px solid #e5e5e5;
        background-color: #ffffff;
      }

      .patty-input {
        flex: 1;
      }

      .send-button {
        background: #0066cc;
        color: #ffffff;
        border: none;
      }

      .send-button:disabled {
        background: #cccccc;
        cursor: not-allowed;
      }

      :host ::ng-deep {
        .patty-avatar-header {
          background-color: #ff6b6b;
          color: #ffffff;
          font-weight: 600;
          width: 50px;
          height: 50px;
          font-size: 1.25rem;
        }

        .patty-avatar-message {
          background-color: #ff6b6b;
          color: #ffffff;
          font-weight: 600;
        }

        .p-avatar {
          width: 40px;
          height: 40px;
        }

        .message-avatar .p-avatar {
          width: 32px;
          height: 32px;
        }
      }
    `,
  ],
})
export class PathwayPattyComponent implements OnInit, AfterViewChecked {
  conversation: PathwayPattyConversation | null = null;
  newMessage = '';
  currentUserInitials = 'U';
  waitingForResponse = false;
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;

  constructor(
    private pattyService: MockPathwayPattyService,
    private authService: AuthService
  ) {
    this.currentUserInitials = this.authService.getUserInitials();
  }

  ngOnInit() {
    this.loadConversation();
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  loadConversation() {
    this.pattyService.getConversation().subscribe({
      next: (conversation) => {
        this.conversation = conversation;
        setTimeout(() => this.scrollToBottom(), 100);
      },
      error: (err) => {
        console.error('Failed to load conversation', err);
      },
    });
  }

  sendMessage() {
    if (!this.newMessage.trim() || this.waitingForResponse || !this.conversation) {
      return;
    }

    const messageText = this.newMessage.trim();
    this.newMessage = '';
    this.waitingForResponse = true;

    this.pattyService.sendMessage(messageText).subscribe({
      next: (response) => {
        // Reload conversation to get both user message and Patty's response
        this.loadConversation();
        this.waitingForResponse = false;
        setTimeout(() => this.scrollToBottom(), 100);
      },
      error: (err) => {
        console.error('Failed to send message', err);
        this.waitingForResponse = false;
      },
    });
  }

  exploreCategory(category: string) {
    // Navigate to feed with category selected
    // Using window.location to ensure full page reload with query params
    window.location.href = `/feed?category=${encodeURIComponent(category)}`;
  }

  scrollToBottom() {
    if (this.messagesContainer) {
      const container = this.messagesContainer.nativeElement;
      container.scrollTop = container.scrollHeight;
    }
  }

  formatMessageTime(date: Date): string {
    const now = new Date();
    const messageDate = new Date(date);
    const diffMs = now.getTime() - messageDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else {
      return messageDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }
  }

  formatMessageText(text: string): string {
    // Convert **text** to <strong>text</strong>
    return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  }
}

